import React, { useEffect, useState } from "react";
import { Trans } from "next-i18next";
import { useRecoilValue } from "recoil";

import { BooleanStringV2 } from "@sellernote/shared/src/api/dto/entities/common/common";
import {
  RequestGuideType,
  RequestGuideUserInfo,
} from "@sellernote/shared/src/api/dto/entities/common/support";
import { UserInfo } from "@sellernote/shared/src/api/dto/entities/common/user";
import USER_QUERY from "@sellernote/shared/src/api/queries/forwarding/USER_QUERY";
import { useCheckIsMobile } from "@sellernote/shared/src/utils/hook";
import useOnceActivatedIntersectionObserver from "@sellernote/features/src/common/hooks/useOnceActivatedIntersectionObserver";
import { FORWARDING_AUTH_ATOMS } from "@sellernote/features/src/forwarding/recoil-states/auth";
import useCheckIsFulfillmentPage from "@sellernote/shipda-kr/src/hooks/common/useCheckIsFulfillmentPage";
import Modal from "@sellernote/ui/src/sds-v2/components/modal/Modal";

import CommonStyled from "../index.styles";
import RequestGuideForm from "./RequestGuideForm";
import SimpleRegisterModal from "./SimpleRegisterModal";
import Styled from "./index.styles";

export type ServiceGuideRequestForm = {
  type?: RequestGuideType;
  userComment?: string;
} & RequestGuideUserInfo;

const initialState: ServiceGuideRequestForm = {
  name: "",
  company: "",
  email: "",
  phone: "",
  userComment: "",
};

// intro 오른쪽에 떠있는 floating UI (mobile 에서는 모달) 와 section 형태의 UI 두가지 스타일.
export type RequestServiceGuideUiType = "floating" | "section";

type SectionUiTypeProps = {
  uiType: "section";
};

type FloatingUiTypeProps = {
  uiType: "floating";
  opensRequestGuideFormModal: boolean;
  handleRequestGuideFormModalClose: () => void;
};

export default function LandingSectionRequestServiceGuide(
  propsByTypes: SectionUiTypeProps | FloatingUiTypeProps
) {
  const { isFulfillmentPage } = useCheckIsFulfillmentPage();

  const { isMobile } = useCheckIsMobile();

  const { target, isActivatedTarget } = useOnceActivatedIntersectionObserver({
    threshold: 0.3,
  });
  const loggedIn = useRecoilValue(FORWARDING_AUTH_ATOMS.USER_LOGGED_IN);

  const [requestGuideForm, setRequestGuideForm] =
    useState<ServiceGuideRequestForm>(initialState);

  const [agreesPrivacyPolicy, setAgreesPrivacyPolicy] =
    useState<BooleanStringV2>("FALSE");

  const [agreesAdEmails, setAgreesAdEmails] =
    useState<BooleanStringV2>("FALSE");

  const getInitialStateOfAlreadyMember = (userInfo: UserInfo) => {
    const { name, company, email, phone } = userInfo;

    setRequestGuideForm({ name, company, email, phone });
  };

  const { data: userInfo } = USER_QUERY.useGetUserInfo({
    enabled: Boolean(loggedIn),
  });

  const [opensSuccessRequestGuideModal, setOpensSuccessRequestGuideModal] =
    useState(false);

  const [opensSimpleRegisterModal, setOpensSimpleRegisterModal] =
    useState(false);

  const resetRequestGuideForm = () => {
    if (loggedIn && userInfo) {
      getInitialStateOfAlreadyMember(userInfo);
    } else {
      setRequestGuideForm(initialState);
    }

    setAgreesPrivacyPolicy("FALSE");
    setAgreesAdEmails("FALSE");
  };

  // section PC, mobile 과 floating PC 의 검은 배경인 경우, 커스텀 스타일 적용
  const isCustomStyle =
    propsByTypes.uiType === "section" ||
    (propsByTypes.uiType === "floating" && !isMobile);

  useEffect(() => {
    if (!userInfo) return;

    getInitialStateOfAlreadyMember(userInfo);
  }, [userInfo]);

  return (
    <Styled.sectionRequestServiceGuide
      uiType={propsByTypes.uiType}
      isCustomStyle={isCustomStyle}
      ref={target}
      className={`${isActivatedTarget ? "active" : ""}`}
    >
      {propsByTypes.uiType === "floating" && isMobile ? (
        <>
          <Modal
            uiType="formInput"
            active={propsByTypes.opensRequestGuideFormModal}
            title={
              isFulfillmentPage ? (
                <Trans i18nKey="components:landing.풀필먼트_소개서_요청" />
              ) : (
                <Trans i18nKey="main-landing:쉽다소개서요청섹션_타이틀" />
              )
            }
            desc={<Trans i18nKey="main-landing:쉽다소개서요청섹션_설명" />}
            body={
              <RequestGuideForm
                form={requestGuideForm}
                setForm={setRequestGuideForm}
                uiType={propsByTypes.uiType}
                isCustomStyle={isCustomStyle}
                {...{
                  setOpensSuccessRequestGuideModal,
                  resetRequestGuideForm,
                  agreesPrivacyPolicy,
                  setAgreesPrivacyPolicy,
                  agreesAdEmails,
                  setAgreesAdEmails,
                }}
              />
            }
            onClose={propsByTypes.handleRequestGuideFormModalClose}
          />
        </>
      ) : (
        <>
          <CommonStyled.sectionTitle>
            {isFulfillmentPage ? (
              <Trans i18nKey="components:landing.풀필먼트_소개서_요청" />
            ) : (
              <Trans i18nKey="main-landing:쉽다소개서요청섹션_타이틀" />
            )}
          </CommonStyled.sectionTitle>

          <em>
            <Trans i18nKey="main-landing:쉽다소개서요청섹션_설명" />
          </em>

          <RequestGuideForm
            form={requestGuideForm}
            setForm={setRequestGuideForm}
            uiType={propsByTypes.uiType}
            isCustomStyle={isCustomStyle}
            {...{
              setOpensSuccessRequestGuideModal,
              resetRequestGuideForm,
              agreesPrivacyPolicy,
              setAgreesPrivacyPolicy,
              agreesAdEmails,
              setAgreesAdEmails,
            }}
          />
        </>
      )}

      <Modal
        uiType="onlyDescription"
        active={opensSuccessRequestGuideModal}
        desc={<Trans i18nKey="main-landing:소개서신청폼_성공_미가입자_모달" />}
        actionPositive={{
          label: (
            <Trans i18nKey="main-landing:소개서신청폼_성공_미가입자_모달_버튼_예" />
          ),
          handleClick: () => {
            setOpensSimpleRegisterModal(true);
            setOpensSuccessRequestGuideModal(false);
          },
        }}
        actionNegative={{
          label: (
            <Trans i18nKey="main-landing:소개서신청폼_성공_미가입자_모달_버튼_아니오" />
          ),
          handleClick: () => {
            resetRequestGuideForm();

            setOpensSuccessRequestGuideModal(false);

            propsByTypes.uiType === "floating" &&
              propsByTypes.handleRequestGuideFormModalClose();
          },
        }}
      />

      <SimpleRegisterModal
        active={opensSimpleRegisterModal}
        requestGuideForm={requestGuideForm}
        handleSimpleRegisterModalClose={() => {
          setOpensSimpleRegisterModal(false);

          resetRequestGuideForm();

          propsByTypes.uiType === "floating" &&
            propsByTypes.handleRequestGuideFormModalClose();
        }}
      />
    </Styled.sectionRequestServiceGuide>
  );
}
